/**
 * ScriptStep — single open step body: script block + hint + field rows + Heard.
 *
 * Each field row renders the SCHEMA-appropriate widget (FormFieldRenderer: text /
 * select / datepicker / textarea …) for every state — filled fields show the widget
 * pre-populated (a select shows its option label, a date shows the DatePicker), never
 * plain read-text. `readOnly` renders the widget DISABLED. The exceptions:
 *   - heard → a chip ("on recording · mm:ss"); click to type the value now
 *   - empty + heard-eligible → the widget plus a "Heard" button (via canMarkHeard)
 *   - heard (live call) → the widget outlined, plus an "Undo" button that unmarks it
 */
import { Markdown } from '@components/Markdown';
import { RotateCcw } from 'lucide-react';
import React from 'react';

import { cn } from '@/utils';
import {
  groupFieldsBySection,
  isResolvedFieldRequired,
  type ResolvedField,
  type ScriptUnit,
} from '@/utils/humanCallModel';

import { canMarkHeard, heardTimestamp } from './heard';
import { FieldControl, fieldEvidenceCluster } from './ScriptFieldParts';
import { centerInScrollParent, flashReveal } from './scrollInPane';

/**
 * Unit header status as a compact COUNT PILL (rendered by ScriptFlow), replacing the
 * old "N pending" / "done" text. `count` is the number of required fields in the unit
 * still to capture (required AND not yet engaged — filled / on file / heard); `done` is
 * true when that count is 0. ScriptFlow renders it with the same green(0)/red(>0) scheme
 * the old right-board "Missed" count pill used. Sections with NO required field return
 * `null` (no pill) — there is nothing to count.
 *
 * A required field counts as still-to-capture unless it is engaged: `captured` (value
 * this session), `prefilled` (on file), or `heard` (acknowledged on the recording — heard
 * counts as done, matching `unitProgress`). This unifies the old `pending` and
 * `to-collect` states into a single red count and `done` into a green 0.
 */
export function unitBadge(unit: ScriptUnit): { count: number; done: boolean } | null {
  const required = unit.fields.filter(isResolvedFieldRequired);
  if (required.length === 0) return null;
  const remaining = required.filter(
    f => f.status !== 'captured' && f.status !== 'prefilled' && f.status !== 'heard',
  ).length;
  return { count: remaining, done: remaining === 0 };
}

interface ScriptStepProps {
  unit: ScriptUnit;
  readOnly: boolean;
  /**
   * Output metadata `fields` tree for the call, keyed by the same paths the script uses
   * (`drug[0].coverage.copay`). Present once extraction has run; drives the per-field
   * evidence, chain-of-thought and heard-seek affordances (BV-1575).
   */
  outputMetaFields?: Record<string, unknown>;
  /** The call's EXTRACTED metadata `fields` tree — the source that actually carries
   *  per-field evidence/reasoning. Same path keys as the data tree. */
  extractedMetaFields?: Record<string, unknown>;
  /** The extracted VALUES for this call — powers the per-field Accept affordance. */
  extractedData?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  /** True only while the call is live. Gates the "Heard" button in each field row. */
  callActive: boolean;
  heardAt: (key: string) => number | undefined;
  callStartMs?: number;
  onFieldChange: (key: string, value: unknown) => void;
  onFieldCommit: (key?: string) => void;
  onToggleHeard: (key: string) => void;
  caseRecordId?: string;
  /** Validation messages for this step's fields, keyed by field key (BV-2129). */
  fieldErrors?: Record<string, string>;
  /** Field to bring into view (set by ScriptFlow.revealField); the matching row scrolls
   *  itself there once rendered, then calls `onRevealed`. */
  revealKey?: string | null;
  onRevealed?: () => void;
}

/** The Undo button's tooltip: when the field was marked, then what a click does. */
function undoHeardTitle(at: number | undefined, callStartMs?: number): string {
  const t = heardTimestamp(at, callStartMs);
  return `${t ? `Marked heard at ${t}` : 'Marked heard'} — click to undo`;
}

/** mm:ss into the call when the start time is known, else a clock time. */
const FieldRow: React.FC<{
  field: ResolvedField;
  readOnly: boolean;
  /** See {@link ScriptStepProps.outputMetaFields}. */
  outputMetaFields?: Record<string, unknown>;
  /** The call's EXTRACTED metadata `fields` tree — the source that actually carries
   *  per-field evidence/reasoning. Same path keys as the data tree. */
  extractedMetaFields?: Record<string, unknown>;
  /** The extracted VALUES for this call — powers the per-field Accept affordance. */
  extractedData?: Record<string, unknown>;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  /** True only while the call is live. Gates the "Heard" button. */
  callActive: boolean;
  heardAt: (key: string) => number | undefined;
  callStartMs?: number;
  onFieldChange: (key: string, value: unknown) => void;
  onFieldCommit: (key?: string) => void;
  onToggleHeard: (key: string) => void;
  caseRecordId?: string;
  /** Validation messages keyed by field key (BV-2129). Carried as the whole map rather
   *  than a resolved message so it rides the shared `rowHandlers` spread — the section
   *  sub-components that render rows do not otherwise have it in scope. */
  fieldErrors?: Record<string, string>;
  /** When true, always render a fixed-width trailing slot for the "Heard" button (empty
   *  when this row doesn't show one). Set per SECTION when any field in it is heard-eligible,
   *  so every input in the section is the SAME width and the two grid columns line up —
   *  otherwise a Heard button steals flex-width from only some inputs and the grid reads ragged. */
  reserveHeardSlot: boolean;
  /** See {@link ScriptStepProps.revealKey}. */
  revealKey?: string | null;
  onRevealed?: () => void;
}> = ({
  field,
  readOnly,
  callActive,
  heardAt,
  callStartMs,
  onFieldChange,
  onFieldCommit,
  onToggleHeard,
  caseRecordId,
  outputMetaFields,
  extractedMetaFields,
  extractedData,
  onNavigateToCall,
  onNavigateToAudit,
  fieldErrors,
  reserveHeardSlot,
  revealKey,
  onRevealed,
}) => {
  // Scrolled from the row itself rather than from ScriptFlow: the row may only mount a
  // commit or two after the reveal was asked for (its step and its section sub-group
  // both have to open first), and its own effect is what runs once it exists.
  const rowRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (revealKey !== field.key || !rowRef.current) return;
    centerInScrollParent(rowRef.current);
    flashReveal(rowRef.current);
    onRevealed?.();
  }, [revealKey, field.key, onRevealed]);

  const isEmpty = field.status === 'missing' || field.status === 'optional';
  // A heard mark is undoable for as long as the call is live — the same window it can be
  // set in (the workspace's toggleHeard is a no-op once the call ends).
  const canUndoHeard = callActive && field.status === 'heard';
  const isRequired = isResolvedFieldRequired(field);

  const label = (
    <div className="flex w-[200px] shrink-0 gap-1.5 text-[12.5px] font-semibold text-foreground">
      {/* Quiet required marker (BI-1467): a small red dot before the label — prominent
          enough to catch the eye while scrolling, without the wall of red the "REQUIRED"
          text pill created when most fields in a section are required. The dot's gutter is
          reserved for EVERY field (transparent when optional) so all labels stay left-aligned. */}
      <span
        className={cn(
          'mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full',
          isRequired ? 'bg-destructive' : 'bg-transparent',
        )}
        aria-hidden="true"
      />
      <span className="min-w-0">
        {field.field.label}
        {isRequired && <span className="sr-only"> (required)</span>}
      </span>
    </div>
  );

  // The Review / chain-of-thought / heard-seek trio, once extraction has run — shared
  // with the multi-code grid's cells (ScriptFieldParts).
  const evidenceCluster = fieldEvidenceCluster({
    field,
    outputMetaFields,
    extractedMetaFields,
    onNavigateToCall,
    onNavigateToAudit,
  });
  const hasEvidenceCluster = evidenceCluster !== null;

  // Everything else (captured / prefilled / missing / optional, or a heard field being
  // typed): the schema widget, disabled when read-only. Heard button only when empty.
  return (
    // data-field-id is what scrollToFirstOutputError queries for — without it a script
    // field could show an error the "View" action could not jump to (BV-2129). Same
    // attribute and same key the accordion boards use.
    <div
      ref={rowRef}
      // -mx-1.5 px-1.5 nets to zero; it only gives the reveal flash room past the label.
      className="-mx-1.5 flex items-center gap-2 rounded-md px-1.5 py-1.5"
      data-field-id={field.key}
    >
      {label}
      {/* Outlined while heard, matching the multi-code grid's heard cells. */}
      <div className={cn('flex min-w-0 flex-1 rounded-md', canUndoHeard && 'ring-1 ring-primary')}>
        <FieldControl
          field={field}
          readOnly={readOnly}
          extractedData={extractedData}
          onFieldChange={onFieldChange}
          onFieldCommit={onFieldCommit}
          caseRecordId={caseRecordId}
          fieldErrors={fieldErrors}
        />
      </div>
      {/* Fixed-width trailing slot (see `reserveHeardSlot`): holds the Heard button when this
          field can show one, empty otherwise — so every input in the section is the same width. */}
      {hasEvidenceCluster && (
        <div className="flex shrink-0 items-center gap-0.5">{evidenceCluster}</div>
      )}
      {/* Undo stays beside the evidence cluster: marking a field heard is what adds its
          heard-seek ("jump to the transcript") entry to that cluster. */}
      {reserveHeardSlot && (!hasEvidenceCluster || canUndoHeard) && (
        <div className="flex w-16 shrink-0 justify-end">
          {callActive && isEmpty && canMarkHeard(field) && (
            <button
              type="button"
              onClick={() => onToggleHeard(field.key)}
              className="shrink-0 whitespace-nowrap rounded-md border border-attention-border bg-attention/40 px-2.5 py-1 text-[11px] font-semibold text-attention-foreground hover:bg-attention/60"
              title="Rep said it out loud — recover the value from the recording after the call"
            >
              Heard
            </button>
          )}
          {canUndoHeard && (
            <button
              type="button"
              onClick={() => onToggleHeard(field.key)}
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
              title={undoHeardTitle(heardAt(field.key), callStartMs)}
              aria-label={`Undo heard for ${field.field.label}`}
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
  // autoFocus handled by ScriptFlow scrolling/focus; FormFieldRenderer autofocus is optional.
};

/** Shared FieldRow props (everything but the field itself, passed down through groups).
 *  `reserveHeardSlot` is computed per section, so it's excluded here and passed explicitly. */
type RowHandlers = Omit<React.ComponentProps<typeof FieldRow>, 'field' | 'reserveHeardSlot'>;

/** A section reserves the trailing Heard slot when the call is live AND at least one of its
 *  fields is heard-eligible — then every input in the section shares one width (columns align). */
function sectionReservesHeardSlot(fields: ResolvedField[], callActive: boolean): boolean {
  return callActive && fields.some(canMarkHeard);
}

/**
 * A collapsible sub-section grouping a nested object's fields (e.g. "Coverage") within a
 * step — restores the output's nested-accordion shape instead of a flat list. Open by
 * default; a header count nudges toward still-missing fields. Ungrouped fields (no
 * section) render without this wrapper.
 */
const SectionGroup: React.FC<{ section: string; fields: ResolvedField[] } & RowHandlers> = ({
  section,
  fields,
  ...rowHandlers
}) => {
  const [open, setOpen] = React.useState(true);
  // A reveal aimed at one of this group's fields reopens it if the user collapsed it —
  // the row is not in the DOM to scroll to otherwise.
  const { revealKey } = rowHandlers;
  React.useEffect(() => {
    if (revealKey && fields.some(f => f.key === revealKey)) setOpen(true);
  }, [revealKey, fields]);
  const pending = fields.filter(f => f.status === 'missing').length;
  const reserveHeardSlot = sectionReservesHeardSlot(fields, rowHandlers.callActive);
  return (
    <div className="rounded-md border border-border/60">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full select-none items-center gap-2 px-2.5 py-1.5 hover:bg-muted/40"
        aria-expanded={open}
      >
        <span
          className={cn(
            'text-[9px] text-muted-foreground transition-transform',
            !open && '-rotate-90',
          )}
          aria-hidden="true"
        >
          ▼
        </span>
        <span className="flex-1 text-left text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
          {section}
        </span>
        {pending > 0 && (
          <span className="rounded-full bg-critical/30 px-1.5 text-[10px] font-semibold text-critical-foreground">
            {pending}
          </span>
        )}
      </button>
      {open && (
        <div className="px-2.5 pb-1">
          {fields.map(field => (
            <FieldRow
              key={field.key}
              field={field}
              reserveHeardSlot={reserveHeardSlot}
              {...rowHandlers}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * A section unit's body (BV-2742): each authored question is a numbered row with the
 * fields that answer it beside it. Only fields that survived the All / Missed / Required
 * filter (`visibleKeys`) render; a question left with none is dropped.
 *
 * Every row is a grid with the same two columns — the question about a third, the fields
 * the rest — so questions and fields line up down the card. Each column has a floor (the
 * fields need room for the 200px label AND a usable input), and when the pane is narrower
 * than both floors the card scrolls sideways instead of squeezing the inputs.
 */
const SectionQuestions: React.FC<
  { questions: NonNullable<ScriptUnit['questions']>; visibleKeys: Set<string> } & RowHandlers
> = ({ questions, visibleKeys, ...rowHandlers }) => {
  const visible = questions
    .map(q => ({ ...q, fields: q.fields.filter(f => visibleKeys.has(f.key)) }))
    .filter(q => q.fields.length > 0);
  const reserveHeardSlot = sectionReservesHeardSlot(
    visible.flatMap(q => q.fields),
    rowHandlers.callActive,
  );
  return (
    // Sideways only: a vertical `auto` (implied by overflow-x) could let a reveal pick this
    // box as the scroller instead of the Call Script pane.
    <div className="overflow-x-auto overflow-y-hidden">
      {/* The column floors plus the gap, so the dividers span a row while it scrolls. */}
      <div className="min-w-[676px] divide-y divide-border/60">
        {visible.map((question, i) => (
          <div
            key={question.questionId}
            className="grid grid-cols-[minmax(220px,1fr)_minmax(440px,1.8fr)] items-start gap-x-4 py-2.5"
            data-question-id={question.questionId}
          >
            <div className="flex min-w-0 gap-2.5 pt-1.5">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
                {i + 1}
              </span>
              <div className="min-w-0 text-[13px] font-medium leading-relaxed text-foreground">
                <Markdown>{question.prompt}</Markdown>
              </div>
            </div>
            <div className="min-w-0">
              {question.fields.map(field => (
                <FieldRow
                  key={field.key}
                  field={field}
                  reserveHeardSlot={reserveHeardSlot}
                  {...rowHandlers}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** A single open step body: script block + hint + field rows (grouped by section). */
export const ScriptStep: React.FC<ScriptStepProps> = ({
  unit,
  readOnly,
  callActive,
  heardAt,
  callStartMs,
  onFieldChange,
  onFieldCommit,
  onToggleHeard,
  caseRecordId,
  outputMetaFields,
  extractedMetaFields,
  extractedData,
  onNavigateToCall,
  onNavigateToAudit,
  fieldErrors,
  revealKey,
  onRevealed,
}) => {
  const rowHandlers: RowHandlers = {
    readOnly,
    callActive,
    heardAt,
    callStartMs,
    onFieldChange,
    onFieldCommit,
    onToggleHeard,
    caseRecordId,
    // Every FieldRow gets these via the spread below. They are OPTIONAL props, so leaving
    // them out compiled cleanly and silently handed each row `undefined` — the evidence,
    // chain-of-thought and heard affordances then never rendered (BV-1575).
    outputMetaFields,
    extractedMetaFields,
    extractedData,
    onNavigateToCall,
    onNavigateToAudit,
    fieldErrors,
    revealKey,
    onRevealed,
  };
  if (unit.questions) {
    return (
      <div className="space-y-2 pl-12 pr-4 pb-3.5">
        {unit.helpText && (
          <div className="ml-0.5 text-[12px] leading-relaxed text-muted-foreground">
            <Markdown>{unit.helpText}</Markdown>
          </div>
        )}
        <SectionQuestions
          questions={unit.questions}
          visibleKeys={new Set(unit.fields.map(f => f.key))}
          {...rowHandlers}
        />
      </div>
    );
  }
  return (
    <div className="space-y-3 pl-12 pr-4 pb-3.5">
      {unit.prompt && (
        <div className="rounded-r-lg border-l-[3px] border-primary/40 bg-primary/5 px-3.5 py-2.5 text-[13px] leading-relaxed text-foreground">
          <Markdown>{unit.prompt}</Markdown>
        </div>
      )}
      {unit.helpText && (
        <div className="ml-0.5 text-[12px] leading-relaxed text-muted-foreground">
          <Markdown>{unit.helpText}</Markdown>
        </div>
      )}
      <div className="space-y-1.5">
        {/* groupFieldsBySection yields ONE group per distinct section (labels unique), so
            keying by label is stable — a disposition recompute won't remount surviving
            SectionGroups (their manual collapse state survives). */}
        {groupFieldsBySection(unit.fields).map(group => {
          if (group.section !== undefined) {
            return (
              <SectionGroup
                key={group.section}
                section={group.section}
                fields={group.fields}
                {...rowHandlers}
              />
            );
          }
          const reserveHeardSlot = sectionReservesHeardSlot(group.fields, callActive);
          return (
            <div key="__ungrouped__">
              {group.fields.map(field => (
                <FieldRow
                  key={field.key}
                  field={field}
                  reserveHeardSlot={reserveHeardSlot}
                  {...rowHandlers}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
